import React from 'react';
import PropTypes from 'prop-types';
import injectScript from '../utils/document/inject-script';
import axios from 'axios';
import { apiRevokeAccessToken } from '../utils/journeyApi';

const GOOGLE_SDK_URL = 'https://apis.google.com/js/api.js';
const EXPECTED_SCOPE = 'https://www.googleapis.com/auth/drive.file';

/**
 * A callback to handle user document selection.
 *
 * @callback onChange
 * @param {Object} data - PickerResponse https://developers.google.com/picker/docs/results
 * @param {Object} google - A reference to the google js api.
 */

/**
 * A wrapper component for the Google Picker API
 *
 * Api docs https://developers.google.com/picker/docs
 * Based on https://github.com/sdoomz/react-google-picker
 *
 * @param {onChange} callback - Called when user selects an item (or cancels).
 */
class GooglePicker extends React.Component {
  static propTypes = {
    children: PropTypes.node,
    accessToken: PropTypes.string,
    mimeTypes: PropTypes.array,
    navHidden: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string,
  };

  static defaultProps = {
    navHidden: false,
    onChange: (data) => {
      console.error('onChange', data);
    },
  };

  componentDidMount() {
    if (this.isGoogleReady()) {
      this.onApiLoad();
    } else {
      injectScript(GOOGLE_SDK_URL).then(this.onApiLoad.bind(this), this.onApiError.bind(this));
    }
  }

  isGoogleReady() {
    return !!window.gapi;
  }

  isGoogleAuthReady() {
    return !!window.gapi.auth;
  }

  isGooglePickerReady() {
    return !!window.google.picker;
  }

  onApiError(response) {
    console.log('onAuthFailed', response);
  }

  /**
   * Use the Google API Loader script to load the google.picker script.
   */
  onApiLoad() {
    window.gapi.load('auth');
    window.gapi.load('picker');
    window.gapi.load('client');
  }

  insertPermission(fileId, value, type, role, callback) {
    var body = {
      ...(value && { value: value }),
      type: type,
      role: role,
    };
    return window.gapi.client.request({
      path: `/drive/v2/files/${fileId}/permissions`,
      method: 'POST',
      body: body,
    });
  }

  updateFilePermissionToPublic(fileId) {
    return this.insertPermission(fileId, null, 'anyone', 'reader');
  }

  /**
   * Look for existing consent. Use it or get it, then build picker.
   */
  async onEngage(tokenID, oauthToken) {
    const resp = await axios
      .get('https://www.googleapis.com/oauth2/v1/tokeninfo', {
        headers: {
          Authorization: `Bearer ${oauthToken}`,
        },
      })
      .catch((err) => err.response);

    let error = null;
    if (resp.status === 400) {
      error = 'Access token was invalid or revoked - please retry.';
    } else if (resp.status === 200 && resp.data.scope.indexOf(EXPECTED_SCOPE) === -1) {
      error = "Access token doesn't have correct permissions - please retry.";
    }

    if (error !== null) {
      this.props.setError(error);
      await apiRevokeAccessToken('google-drive', tokenID);
      await this.props.fetchTokens();
      return null;
    }

    if (!this.isGoogleReady() || !this.isGoogleAuthReady() || !this.isGooglePickerReady() || this.props.disabled) {
      return null;
    }

    if (oauthToken) {
      window.gapi.client.setToken({ access_token: oauthToken });
      this.createPicker(oauthToken);
    }
  }

  /**
   * Initialize a Google Picker
   */
  createPicker(oauthToken) {
    const google = window.google;

    const view = new window.google.picker.View(google.picker.ViewId.DOCS);

    if (this.props.mimeTypes) {
      view.setMimeTypes(this.props.mimeTypes.join(','));
    }

    let picker = new window.google.picker.PickerBuilder()
      .setOAuthToken(oauthToken)
      .addView(view)
      .setCallback((data) => {
        this.props.onChange(data, google, this.updateFilePermissionToPublic.bind(this));
      });

    if (this.props.navHidden) {
      picker.enableFeature(window.google.picker.Feature.NAV_HIDDEN);
    }

    return picker.build().setVisible(true);
  }

  render() {
    return (
      <div onClick={this.onEngage.bind(this, this.props.tokenID, this.props.accessToken)}
        className={this.props.className || ''}>
        {this.props.children ? this.props.children : <button>Pick from Google Drive</button>}
      </div>
    );
  }
}

export default GooglePicker;
